<template>
    <Overlay :show="showLoading">
        <ProductTypeDefinition
            :product="product"
            @suppliers="setSuppliers"
            @done="init"
        >
            <RequestLifeCycle
                ref="requestLifeCycle"
                :life-cycle="request.lifeCycle"
            />
            <b-card ref="productCard">
                <b-row class="pb-1">
                    <b-col>
                    <span class="align-middle">
                        <StatusBadge
                            type="received"
                            :status="request.status"
                        />
                    </span>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12" class="mb-2">
                        <h2 class="float-left pr-1 mb-1">
                            {{ $t(getProductTypeTranslationKey()) }}: {{ getProductTitle() }}
                        </h2>
                        <Actions
                            :request="request"
                            :show-save="isSaveButtonVisible"
                            :is-reply-disabled="isReplyDisabled"
                            :show-reply="isReplyButtonVisible"
                            :show-forward="isForwardVisible"
                            :is-life-cycle-visible="request.lifeCycle.length > 0"
                            @showLifeCycle="showLifeCycle"
                            @forward="forward"
                            @sendRequest="sendRequest"
                            @save="saveAndRefreshSubstanceState"
                            @reply="sendReply"
                            @close="close"
                            class="float-right"
                        />
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12">
                        <div class="tabs">
                            <div>
                                <ul role="tablist" class="nav nav-tabs nav-justified">
                                    <li role="presentation" class="nav-item">
                                        <a :class="{'nav-link': true, active: isTabActive('view-request')}" @click.prevent="onViewRequestTabClick" href="#" target="_self">
                                            {{ $t('Component(ReceivedRequestTabs):view-request-label') }}
                                        </a>
                                    </li>
                                    <li class="d-flex align-items-center p-1">
                                        <feather-icon size="18" icon="ChevronRightIcon" />
                                    </li>
                                    <li role="presentation" class="nav-item">
                                        <a :class="{'nav-link': true, active: isTabActive('general-info')}" @click.prevent="onGeneralInfoClick" href="#" target="_self">
                                            {{ $t('Component(ProductCardTabs):general-info-label') }}
                                        </a>
                                    </li>
                                    <li class="d-flex align-items-center p-1">
                                        <feather-icon size="18" icon="ChevronRightIcon" />
                                    </li>
                                    <li role="presentation" class="nav-item">
                                        <a :class="{'nav-link': true, active: isTabActive('concern-elements'), disabled: !isConcernElementsTabAccessable}" role="tab" @click.prevent="onConcernElementsTabClick" href="#" target="_self">
                                            {{ $t('Component(ProductCardTabs):concern-elements-label') }}
                                        </a>
                                    </li>
                                    <li class="d-flex align-items-center p-1">
                                        <feather-icon size="18" icon="ChevronRightIcon" />
                                    </li>
                                    <li role="presentation" class="nav-item">
                                        <a :class="{'nav-link': true, active: isTabActive('composition')}" role="tab" @click.prevent="onCompositionTabClick" href="#" target="_self">
                                            {{ $t('Component(ProductCardTabs):composition-label') }}
                                        </a>
                                    </li>
                                    <li class="d-flex align-items-center p-1">
                                        <feather-icon size="18" icon="ChevronRightIcon" />
                                    </li>
                                    <li role="presentation" class="nav-item">
                                        <a :class="{'nav-link': true, active: isTabActive('verification')}" role="tab" @click.prevent="onVefificationTabClick" href="#" target="_self">
                                            {{ $t('Component(ProductCardTabs):verification-label') }}
                                        </a>
                                    </li>
                                    <li class="d-flex align-items-center p-1">
                                        <feather-icon size="18" icon="ChevronRightIcon" />
                                    </li>
                                    <li role="presentation" class="nav-item">
                                        <a :class="{'nav-link': true, active: isTabActive('send-reply')}" role="tab" @click.prevent="onSendReplyTabClick" href="#" target="_self">
                                            {{ $t('Component(ReceivedRequestTabs):send-reply-label') }}
                                        </a>
                                    </li>
                                </ul>
                            </div>
                            <div class="tab-content mt-2">
                                <div :class="{active: isTabActive('view-request'), tab: true}">
                                    <ViewRequest
                                        :request="request"
                                    />
                                </div>
                                <div :class="{active: isTabActive('general-info'), tab: true}">
                                    <GeneralInfo
                                        :product="product"
                                        :id="id + ''"
                                        :suppliers="suppliers"
                                        :has-concern-elements="isProductHasConcernElements"
                                        :product-description-document="productDescription"
                                        @productDescriptionChange="productDescriptionChange"
                                        @suppliersChange="suppliersChange"
                                    />
                                </div>
                                <div :class="{active: isTabActive('concern-elements'), tab: true}">
                                    <ConcernElements
                                        key="product-card-flow"
                                        :product="product"
                                        :id="id + ''"
                                        :included-substances="includedSubstances"
                                        :excluded-substances="excludedSubstances"
                                        :legislations="request.legislations.map(l => l.id)"
                                        @listQuestionStateUpdated="listQuestionStateUpdated"
                                        @include="includeConcernSubstance"
                                        @exclude="excludeConcernSubstance"
                                        @undefined="makeSubstanceUndefined"
                                    />
                                </div>
                                <div :class="{active: isTabActive('composition'), tab: true}" style="overflow: visible;">
                                    <Composition
                                        :product="product"
                                        :id="id + ''"
                                        :concern-substances="includedSubstances"
                                        :show-components-substances="product.isComplex"
                                        :legislations="request.legislations.map(l => l.id)"
                                        @substancePropertiesChanged="onSubstancePropertiesChange"
                                        @hasConcernSubstances="hasConcernSubstances"
                                        @substanceDetailsChange="onSubstanceDetailsChange"
                                        @include="includeConcernSubstance"
                                        @exclude="excludeConcernSubstance"
                                    />
                                </div>
                                <div :class="{active: isTabActive('verification'), tab: true}">
                                    <Verification
                                        :product="product"
                                        :id="id + ''"
                                        :doc="declarationsOfConformity"
                                        :evidence="evidenceReports"
                                        :safe-use="safeUseInstructions"
                                        :disassembling="disassemblingInstructions"
                                        :compliant-statement="compliantStatement"
                                        :is-safe-use-applicable="safeUseApplicable"
                                        @safeUseApplicableChange="safeUseApplicableChange"
                                        @addCompliantStatement="addCompliantStatement"
                                        @addDoc="addDoC"
                                        @addReport="addReport"
                                        @removeDoc="removeDoC"
                                        @removeCompliantStatement="removeCompliantStatement"
                                        @removeReport="removeReport"
                                        @safeUseChange="safeUseChange"
                                        @disassemblingChange="disassemblingChange"
                                    />
                                </div>
                                <div :class="{active: isTabActive('send-reply'), tab: true}">
                                    <SendReply
                                        :included-substances="includedSubstances"
                                        :request="request"
                                        :product="product"
                                        :id="id"
                                        :suppliers="suppliers"
                                        :declaration-of-conformity-documents="declarationsOfConformity"
                                        :compliant-statement-documents="compliantStatement"
                                        :evidence-reports="evidenceReports"
                                        :safe-use-document="safeUseInstructions"
                                        :dissembling-instructions-document="disassemblingInstructions"
                                        :is-safe-use-applicable="safeUseApplicable"
                                        :has-concern-substances="product.hasConcernSubstances"
                                        @declarationValidity="onDeclarationValidity"
                                        @otherInformation="onOtherReplyInformation"
                                        @suppliersChange="suppliersChange"
                                        @replyTabIsValid="replyTabIsValid"
                                        @valid="replyTabIsValid"
                                        @invalid="replyTabIsInValid"
                                    />
                                </div>
                            </div>
                        </div>
                    </b-col>
                </b-row>
                <b-row v-if="isReplyButtonVisible">
                    <b-col class="mr-2 pb-1">
                        <small class="float-right">
                            By clicking on the Send Reply button you verify that the above information is correct.
                        </small>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <Actions
                            :request="request"
                            :is-reply-disabled="isReplyDisabled"
                            :show-reply="isReplyButtonVisible"
                            :show-save="isSaveButtonVisible"
                            :show-forward="isForwardVisible"
                            :is-life-cycle-visible="request.lifeCycle.length > 0"
                            @showLifeCycle="showLifeCycle"
                            @forward="forward"
                            @sendRequest="sendRequest"
                            @save="saveAndRefreshSubstanceState"
                            @reply="sendReply"
                            @close="close"
                            class="float-right"
                        />
                    </b-col>
                </b-row>
            </b-card>
        </ProductTypeDefinition>
    </Overlay>
</template>
<script>
import {Conversation} from "@core/components/messenger";
import { BCard, BRow, BCol, BOverlay, BBadge } from 'bootstrap-vue'
import {ProductType, RequestStatus, VerificationDocumentType} from "@/libs/product";
import {ViewRequest, SendReply, Actions} from "../components";
import {GeneralInfo, ConcernElements, Composition, Verification, SaveAndClose, ProductCardMixins} from "@/views/product/components/product-card";
import {ProductApi} from '@core/api'
import Ripple from 'vue-ripple-directive'
import StatusBadge from "@/views/request/components/StatusBadge";
import 'animate.css'
import {ProductBreadcrumbs, ProductProvider, receivedRequestProvider} from "@core/domain/product";
import ProductTypeDefinition from "@core/components/product/ProductTypeDefinition.vue";
import Overlay from "@core/components/common/Overlay.vue";
import RequestLifeCycle from "@/views/request/components/RequestLifeCycle.vue";

export default {
    components: {
        RequestLifeCycle,
        Overlay,
        ProductTypeDefinition,
        BBadge,
        BOverlay,
        BCard,
        BRow,
        BCol,
        StatusBadge,
        GeneralInfo,
        ConcernElements,
        Composition,
        Verification,
        SaveAndClose,
        ViewRequest,
        SendReply,
        Actions,
        Conversation,
    },

    directives: {
        Ripple,
    },

    props: {
        id: {},
        pid: {
            type: String
        },
    },

    data() {
        return {
            isReplyDisabled: true,
            isReplyButtonVisible: false,
            isSaveButtonVisible: false,
            showLoading: true,

            selectedEvidenceReports: [],
            selectedDeclarationsOfConformity: [],

            isSafeUseSelected: false,
            isDissemblingSelected: false,

            request: {
                legislations: [],
                verificationDocuments: [],
                informationLevel: '',
                customer: {
                    title: ''
                },
                product: {
                    names: [{value: ''}],
                    identifiers: [{value: ''}],
                },
                validTo: null,
                substanceState: null,
                lifeCycle: [],
                status: null
            },

            concernElements: [],

            declaration: null,
            internalComment: '',
            customerInformation: '',
            declarationValidity: {month: 6},

            activeTab: 'view-request',
            statuses: RequestStatus,

            ...ProductCardMixins.data,
        };
    },

    methods: {
        onTabClick(tab) {
            this.activeTab = tab;
        },
        onViewRequestTabClick() {
            this.activeTab = 'view-request';
            this.hideSaveButton();
        },
        onGeneralInfoClick() {
            this.activeTab = 'general-info';
            this.showSaveButton();
        },
        onCompositionTabClick() {
            this.activeTab = 'composition';
            this.showSaveButton();
        },
        onVefificationTabClick() {
            this.activeTab = 'verification';
            this.showSaveButton();
        },
        onSendReplyTabClick() {
            this.activeTab = 'send-reply';
            this.hideSaveButton();
            this.onReplyTabSelect();
        },
        onConcernElementsTabClick() {
            this.activeTab = 'concern-elements';
            this.showSaveButton();
        },
        isTabActive(tab) {
            return this.activeTab === tab;
        },
        safeUseApplicableChange(isApplicable) {
            this.safeUseApplicable = isApplicable;
        },

        onDeclarationValidity(value) {
            this.declarationValidity = value;
        },

        onOtherReplyInformation(information) {
            this.internalComment = information.internalComment;
            this.customerInformation = information.customerInformation;
        },

        showSaveButton() {
            this.isReplyButtonVisible = false;
            this.isSaveButtonVisible = true;
        },

        hideSaveButton() {
            this.isSaveButtonVisible = false;
        },

        onReplyTabSelect() {
            this.hideSaveButton();

            this.isReplyDisabled = false;
            this.isReplyButtonVisible = true;
        },

        replyTabIsValid(declaration) {
            this.isReplyDisabled = false;

            this.declaration = declaration;
        },

        replyTabIsInValid(declaration) {
            // this.isReplyDisabled = true;
            this.declaration = declaration;
        },

        sendReply() {
            this.declaration.internalComment = this.internalComment;
            this.declaration.customerInformation = this.customerInformation;
            this.declaration.declarationValidity = this.declarationValidity

            this.isReplyDisabled = true;
            this.showLoading = true;

            //  covers case when user removes a document
            //  todo refactor
            this.declaration.documents = this.declaration.documents.filter(d => {
                const typeDocumentsMap = {}
                typeDocumentsMap[VerificationDocumentType.EVIDENCE_REPORT] = this.evidenceReports;
                typeDocumentsMap[VerificationDocumentType.DECLARATION_OR_CONFORMITY] = this.declarationsOfConformity;
                typeDocumentsMap[VerificationDocumentType.COMPLIANT_STATEMENT] = this.compliantStatement;

                if (typeDocumentsMap.hasOwnProperty(d.type)) {
                    for (let i = 0; i < typeDocumentsMap[d.type].length; ++i) {
                        if (d.id === typeDocumentsMap[d.type][i].id) {
                            return true;
                        }
                    }
                    return false;
                } else {
                    return true;
                }
            });

            ProductApi
                .replyToRequest(this.id, this.declaration)
                .then(() => {
                    this.request.status = RequestStatus.REPLIED;
                    this.$swal({
                        title: 'Good job!',
                        text: 'Declaration has been sent to a customer!',
                        icon: 'success',
                        timer: 1000,
                        allowOutsideClick: true,
                        customClass: {
                            confirmButton: 'btn btn-primary',
                        },
                        buttonsStyling: false,
                    });
                })
                .finally(() => {
                    this.showLoading = false;
                    this.isReplyDisabled = false;
                })
            ;
        },

        sendRequest() {
            this.$router.push({
                name: 'product-send-request',
                params: {id: this.pid, product: this.product},
                props: {product: this.product}
            });
        },

        close() {
            this.$router.push({
                name: 'received-requests',
            });
        },

        saveAndRefreshSubstanceState() {
            this.showLoading = true;
            this
                .save(true, false, false, false)
                .then(() => {
                    const request = receivedRequestProvider(this.id);
                    return request.getRequest().then(() => request.refreshSubstanceState());
                })
                .finally(() => this.showLoading = false)
            ;
        },

        showLifeCycle() {
            this.$refs.requestLifeCycle.show();
        },

        forward() {
            if (this.$store.getters.showSendEmailSettingsStep) {
                this.$router.push({
                    name: 'request-email-options',
                    query: {
                        n: 'forward-request',
                        nid: this.id,
                        b: 'received-request',
                        bid: this.id
                    }
                });
            } else {
                this.$router.push({
                    name: 'forward-request',
                    params: {id: this.id, req: this.request},
                    props: {request: this.request},
                });
            }
        },

        getProductTypeTranslationKey() {
            switch (this.product.type) {
                case ProductType.GROUP:
                    return 'Component(Text):product-type-group'

                case ProductType.OWN:
                    return 'Component(Text):product-type-own'

                case ProductType.PURCHASED:
                    return 'Component(Text):product-type-purchased'

                default:
                    return '';
            }
        },

        getProductTitle() {
            if (this.product.names.length > 0) {
                return this.product.names[0].value;
            }

            return '';
        },

        fetch() {
            return new Promise((resolve, reject) => {
                this.fetchProductRequest().then(() => {
                    resolve();

                    this.fetchSubstances();
                    this.fetchDocuments();
                });
            });
        },

        fetchProductRequest() {
            return receivedRequestProvider(this.id)
                .getRequest()
                .then(request => {
                    this.request = request.request;
                    this.product = request.product.card;
                    this.safeUseApplicable = this.product.isSafeUseApplicable
                });
        },

        ...ProductCardMixins.methods,

        getProductId() {
            return this.request ? this.request.product.id : null;
        },

        initProductBreadcrumbs() {
            const requestId = this.id;
            ProductBreadcrumbs.push(
                'Received request',
                this.getProductTitle(),
                () => {
                    this.$router.push({
                        name: 'received-request',
                        params: {
                            id: requestId
                        }
                    })
                }
            )
        },
    },

    created() {
        this.reInitData();
        this.fetch().then(() => {
            this.initProductBreadcrumbs();
            setTimeout(() => {
                this.showLoading = false;
            }, 200);
        });
    },
    computed: {
        ...ProductCardMixins.computed,

        isForwardVisible() {
            if (this.product.type === ProductType.OWN) {
                return this.product.isComplex;
            }
            return true;
        }
    }
};
</script>
<style lang="scss">
.tab-content {
    .tab {
        transition: opacity 0.25s ease-in-out;
        opacity: 0;
        height: 0;
        overflow: hidden;
    }
    .tab.active {
        opacity: 1;
        height: auto;
    }
}
</style>
